import {TreeNode} from "../../../components/tree/Tree";
import {FinancialEntityType, RevenueModelType} from "../../../__generated__/generated_types";
import {Property} from "../../../contexts/properties/logic";
import {FinancialEntity} from "../../../contexts/chartofaccounts/logic";
import {AuthUser} from "../../../contexts/auth_types";
import {
    adminModelDownEnabled,
    isOldOperationalMetricsUI,
    showLossLeaders,
    showUserSpecifiedUnitCounts
} from "../../../global/feature_checkers";
import {userIsAdmin} from "../../../authorization/AuthorizationCheckers";

export enum AccountRouteParentComponents {
    OPERATIONAL = "/operational",
    RENOVATIONS = "/renovations",
    PAYROLL = "/payroll",
    ACCOUNT = "/account",
    // This must be kept in sync with the value in AccountRouteConstants.ts
    SIMPLIFIED_REVENUE = "/simplified-revenue",
    AFFORDABLE = "/affordable"
}

export const ACCOUNT_ROUTE_PARENT_COMPONENTS = [
    AccountRouteParentComponents.OPERATIONAL,
    AccountRouteParentComponents.RENOVATIONS,
    AccountRouteParentComponents.PAYROLL,
    AccountRouteParentComponents.ACCOUNT,
    AccountRouteParentComponents.SIMPLIFIED_REVENUE,
    AccountRouteParentComponents.AFFORDABLE
];

export const OPERATIONAL_PREFIX = AccountRouteParentComponents.OPERATIONAL;
export const ACCOUNT_PREFIX = AccountRouteParentComponents.ACCOUNT;

// BEGIN: Original Revenue Routing Constants
export const ORIGINAL_REVENUE_CHILD_TREE_NODES: TreeNode[] = [
    {
        title: "Avg Market Rents",
        url: `${OPERATIONAL_PREFIX}/average-rent`,
    },
    {
        title: "Renewal Increase",
        url: `${OPERATIONAL_PREFIX}/renewal-rates`,
    },
    {
        title: "Occupancy Manager",
        url: `${OPERATIONAL_PREFIX}/occupancy-manager`,
    },
    {
        title: "Move-In Discount",
        url: `${OPERATIONAL_PREFIX}/move-in-discount`,
    },
    {
        title: "Admin, Model, and Down Units",
        url: `${OPERATIONAL_PREFIX}/admin-model-down`,
    },
    {
        title: "Early Terms",
        url: `${OPERATIONAL_PREFIX}/early-terminations`,
    },
    {
        title: "Month to Month Move Outs",
        url: `${OPERATIONAL_PREFIX}/month-to-month-move-outs`,
    },
    {
        title: "Unit Count Adjustment",
        url: `${OPERATIONAL_PREFIX}/user-specified-unit-count`,
    },
];

function originalRevenueTreeForUser(user: AuthUser | null): TreeNode[] {
    let treeNodes = ORIGINAL_REVENUE_CHILD_TREE_NODES;
    if(!showLossLeaders(user)) {
        treeNodes = treeNodes.filter(v => v.title !== "Move-In Discount");
    }
    if(!adminModelDownEnabled(user)) {
        treeNodes = treeNodes.filter(v => v.title !== "Admin, Model, and Down Units");
    }
    if(!showUserSpecifiedUnitCounts(user) || !userIsAdmin(user)) {
        treeNodes = treeNodes.filter(v => v.title !== "Unit Count Adjustment");
    }
    return treeNodes;
}

export const ORIGINAL_REVENUE_CHILD_OLD_OP_METRIC_TREE_NODES: TreeNode[] = [
    {
        title: "Expirations",
        url: `${OPERATIONAL_PREFIX}/lease-expirations`,
    },
    {
        title: "Renewal Ratio",
        url: `${OPERATIONAL_PREFIX}/renewal-ratios`,
    },
    {
        title: "Move Outs",
        url: `${OPERATIONAL_PREFIX}/move-outs`,
    },
    {
        title: "Occupancy",
        url: `${OPERATIONAL_PREFIX}/occupancy-rates`,
    },
];

const ORIGINAL_REVENUE_SUMMARY_TREE_NODE: TreeNode = {
    title: "Summary",
    url: `${OPERATIONAL_PREFIX}/summary`
};
// END: Original Revenue Routing Constants

// BEGIN: Simplified Revenue Routing Constants
export const SIMPLIFIED_REVENUE_PREFIX = AccountRouteParentComponents.SIMPLIFIED_REVENUE;

export const SIMPLIFIED_REVENUE_ROUTES = [
    "lease-expiration-schedule",
    "market-rent",
    "renewal-trade-out",
    "renewal-ratios",
    "renovations",
    "vacancy-rates",
    "summary"
];

export const SIMPLIFIED_REVENUE_CHILD_TREE_NODES: TreeNode[] = [
    {
        title: 'Lease Expiration Schedule',
        url: `${SIMPLIFIED_REVENUE_PREFIX}/lease-expiration-schedule`,
        useRawUrl: true,
    },
    {
        title: 'Market Rent',
        url: `${SIMPLIFIED_REVENUE_PREFIX}/market-rent`,
        useRawUrl: true,
    },
    {
        title: 'Renewal Trade Out',
        url: `${SIMPLIFIED_REVENUE_PREFIX}/renewal-trade-out`,
        useRawUrl: true,
    },
    {
        title: 'Renewal Ratio',
        url: `${SIMPLIFIED_REVENUE_PREFIX}/renewal-ratios`,
        useRawUrl: true,
    },
    {
        title: 'Renovations',
        url: `${SIMPLIFIED_REVENUE_PREFIX}/renovations`,
        useRawUrl: true,
    },
    {
        title: 'Vacancy Rates',
        url: `${SIMPLIFIED_REVENUE_PREFIX}/vacancy-rates`,
        useRawUrl: true,
    },
    {
        title: 'Summary',
        url: `${SIMPLIFIED_REVENUE_PREFIX}/summary`,
        useRawUrl: true,
    },
];
// END: Simplified Revenue Routing Constants

// BEGIN: Affordable Revenue Routing Constants
export const AFFORDABLE_REVENUE_PREFIX = AccountRouteParentComponents.AFFORDABLE;

export const AFFORDABLE_REVENUE_CHILD_TREE_NODES = [
    {
        title: "Non-Subsidized Market Rents",
        url: `${OPERATIONAL_PREFIX}/average-rent`,
    },
    {
        title: "Non-Subsidized Expirations",
        url: `${OPERATIONAL_PREFIX}/lease-expirations`,
    },
    {
        title: "Non-Subsidized Move Outs",
        url: `${OPERATIONAL_PREFIX}/move-outs`,
    },
    {
        title: "Non-Subsidized Renewal Increase",
        url: `${OPERATIONAL_PREFIX}/renewal-rates`,
    },
    {
        title: "Non-Subsidized Move-Ins",
        url: `${AFFORDABLE_REVENUE_PREFIX}/conventional/move-in`
    },
    {
        title: "Subsidized Max Rent",
        url: `${AFFORDABLE_REVENUE_PREFIX}/max-rent`,
    },
    {
        title: "Subsidized Move-Ins",
        url: `${AFFORDABLE_REVENUE_PREFIX}/move-in`
    },
    {
        title: "Subsidized Move-Outs",
        url: `${AFFORDABLE_REVENUE_PREFIX}/move-out`,
    },
    {
        title: "Subsidized In-Place Rent",
        url: `${AFFORDABLE_REVENUE_PREFIX}/ipr-change`,
    },
    {
        title: "Summary",
        url: `${AFFORDABLE_REVENUE_PREFIX}/summary`,
        useRawUrl: true
    }
];
// END: Affordable Revenue Routing Constants

// BEGIN: Unit Level Revenue Routing Constants

// NOTE: Unit level is deprecated, but we keep it around
// because some of the code still lives on. Viva La Unit Level!!!!
export const UNIT_LEVEL_CHILD_TREE_NODES = [
    {
        title: 'Market Rents',
        url: '/revenue/market-rents',
        useRawUrl: true,
    },
    {
        title: 'Leasing Activity',
        url: '/revenue/leasing-activity',
        useRawUrl: true,
    },
    {
        title: 'Summary',
        url: '/revenue/summary',
        useRawUrl: true,
    },
];
// END: Unit Level Revenue Routing Constants

function buildCoaTreeNodesInternal(coa: FinancialEntity[]): TreeNode[] {
    const nodes: TreeNode[] = [];
    for(const component of coa) {
        let children: TreeNode[] | undefined = undefined;
        if(component.children.length > 0) {
            children = buildCoaTreeNodesInternal(component.children);
        }
        nodes.push({
            title: component.name,
            children: children,
            url: component.type === FinancialEntityType.Account ? `${ACCOUNT_PREFIX}/${component.id}` : undefined
        });
    }
    return nodes;
}

function buildCoaTreeNodes(coa: FinancialEntity[]): TreeNode[] {
    // The first level of components are always discarded.
    // Hence, we use the helper internal method to do actual building.
    const nodes: TreeNode[] = [];
    for(const component of coa) {
        nodes.push(...buildCoaTreeNodesInternal(component.children));
    }
    return nodes;
}

export function buildTreeNodes(property: Property, coa: FinancialEntity[], hasPayroll: boolean, renovations: TreeNode[] | null | undefined, user: AuthUser | null): TreeNode[] {
    let revenueChildren: TreeNode[] = [];
    switch(property.revenueModel) {
        case RevenueModelType.Original:
        case RevenueModelType.LeaseUp:
            // only include the deprecated operational metric pages if the client feature is set for them
            if (isOldOperationalMetricsUI(user)) {
                revenueChildren.push(...ORIGINAL_REVENUE_CHILD_OLD_OP_METRIC_TREE_NODES);
            }
            revenueChildren.push(...originalRevenueTreeForUser(user));
            revenueChildren.push(ORIGINAL_REVENUE_SUMMARY_TREE_NODE);
            break;
        case RevenueModelType.Simplified:
            revenueChildren = SIMPLIFIED_REVENUE_CHILD_TREE_NODES.slice();
            break;
        case RevenueModelType.Affordable:
            revenueChildren = AFFORDABLE_REVENUE_CHILD_TREE_NODES.slice();
            break;
        case RevenueModelType.UnitLevel:
            revenueChildren = UNIT_LEVEL_CHILD_TREE_NODES.slice();
            break;
        default:
            console.error("Unknown revenue model: ", property.revenueModel);
            break;
    }

    const accountNodes = buildCoaTreeNodes(coa);

    // Combine everything into the final menu tree.
    const treeNodes: TreeNode[] = [
        {
            title: "Revenue",
            children: revenueChildren,
        }
    ];
    if(renovations && renovations.length > 0) {
        // Renovations is really stupid in the way it works.
        // It's an array with one node, but only matters if it has children.
        // LOL!!!!!
        const node = renovations[0];
        if(node && node.children && node.children.length > 0) {
            treeNodes.push(node);
        }
    }
    if(hasPayroll) {
        treeNodes.push({
            title: "Payroll",
            url:'/payroll',
        });
    }
    treeNodes.push(...accountNodes);
    return treeNodes;
}

function flattenTreeNodes(nodes: TreeNode[]): TreeNode[] {
    const flat: TreeNode[] = [];
    for(const node of nodes) {
        if(typeof node.url === "string" && node.url) {
            flat.push(node);
        }
        if(node.children) {
            flat.push(...flattenTreeNodes(node.children));
        }
    }
    return flat;
}

export function buildPrevNext(sideBar: TreeNode[], categoryTypes: string[]): {prev: Map<string, TreeNode>, next: Map<string, TreeNode>, flat: TreeNode[]} {
    // Flatten out the sidebar tree to a flat list of nodes with URLs.
    const flat = flattenTreeNodes(sideBar);
    const prevMap = new Map<string, TreeNode>();
    const nextMap = new Map<string, TreeNode>();
    for(let i = 0; i < flat.length; i++) {
        const node = flat[i];
        if(node?.url) {
            let prev: TreeNode | undefined = undefined;
            if(i === 0) {
                prev = flat[flat.length - 1];
            } else {
                prev = flat[i - 1];
            }

            let next: TreeNode | undefined = undefined;
            if(i === flat.length - 1) {
                next = flat[0];
            } else {
                next = flat[i + 1];
            }

            const urls = [node.url];
            if(!node.useRawUrl) {
                for(const categoryType of categoryTypes) {
                    urls.push(`${node.url}/${categoryType}`);
                }
            }

            if(prev) {
                for(const url of urls) {
                    prevMap.set(url, prev);
                }
            }
            if(next) {
                for(const url of urls) {
                    nextMap.set(url, next);
                }
            }
        }
    }

    return {
        prev: prevMap,
        next: nextMap,
        flat: flat
    };
}
